import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth, PaymentService, UserData } from '@aaas/shared';

interface CheckoutVariables {
  workspaceId: string;
  variantId: number;
  userProfile?: UserData;
}

/**
 * Mutation hook to create checkout session
 * Invalidates credits and transactions on window focus (when user returns from checkout)
 */
export const useCheckout = () => {
  const { user, currentWorkspace } = useAuth();
  const queryClient = useQueryClient();

  // Listen for window focus to refresh data when user returns from checkout
  useEffect(() => {
    const handleFocus = () => {
      if (currentWorkspace) {
        // Invalidate both credits and transactions when user comes back
        queryClient.invalidateQueries({
          queryKey: ['workspace-credits', currentWorkspace.id],
        });
        queryClient.invalidateQueries({
          queryKey: ['transactions', currentWorkspace.id],
        });
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [queryClient, currentWorkspace]);

  return useMutation({
    mutationFn: async ({ workspaceId, variantId, userProfile }: CheckoutVariables) => {
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Open blank window first to prevent popup blockers
      const checkoutWindow = window.open(
        '',
        '_blank',
        'width=800,height=600,scrollbars=yes,resizable=yes'
      );

      const idToken = await user.getIdToken();
      const checkoutRequest = {
        variant_id: variantId,
        customer_email: userProfile ? PaymentService.getCustomerEmail(userProfile) : undefined,
      };

      const response = await PaymentService.createWorkspaceCheckout(
        idToken,
        workspaceId,
        checkoutRequest
      );

      // If popup was blocked, fallback to current tab navigation
      if (!checkoutWindow) {
        window.location.assign(response.checkout_url);
        return response;
      }

      // Set checkout URL in the already opened window
      checkoutWindow.location.href = response.checkout_url;

      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate credits query after successful checkout initiation
      // Credits will be updated when user completes payment and returns
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['workspace-credits', variables.workspaceId],
        });
        queryClient.invalidateQueries({
          queryKey: ['transactions', variables.workspaceId],
        });
      }, 2000);
    },
    onError: (error) => {
      console.error('Checkout failed:', error);
    },
  });
};
